import { Stack, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import { Card, CardContent, CardHeader } from './CampaignWizardStyles';

import { Campaign } from '../../../../types/Campaign';
import { CampaignWizardStepProps } from './CampaignWizard';
import { Table, TablePrimaryCell } from '../../../../components/Table';
import { useCampaignAdGroupAudienceSourceSummary } from '../../../../hooks/Models/Campaign/CampaignAdGroupAudienceSourceSummary';
import { useCampaignAdGroupGeoLocationSummary } from '../../../../hooks/Models/Campaign/CampaignAdGroupGeoLocationSummary';
import { useCampaignAdGroupInventorySourceSummary } from '../../../../hooks/Models/Campaign/CampaignAdGroupInventorySourceSummary';
import { useCampaignAdGroupLocationSummary } from '../../../../hooks/Models/Campaign/CampaignAdGroupLocationSummary';
import { useCodeList } from '../../../../hooks/Models/CodeList';
import CampaignAudienceTypeSummary from '../../../../features/Campaign/CampaignAudienceTypeSummary';
import Column from '../../../../components/Column';
import Utils from '../../../../components/Utils';
import { getFlightStatusBadge, calculateFlightSummary } from '../../../../utils/FlightStatusUtils';
import { useUserContext } from '../../../../hooks/Contexts/UserContext';

type CampaignReviewQuickSummaryProps = {
    onChange?: (value: Partial<Campaign>) => void;
} & Omit<CampaignWizardStepProps, 'onChange'>;

export default function CampaignReviewQuickSummary(props: CampaignReviewQuickSummaryProps) {
    const { campaign } = props;
    const { userContext } = useUserContext();
    const { getCodeDescription } = useCodeList({ codeGroup: 'CampaignType' });

    // const locationCount = campaign?.adGroups?.[0]?.adGroupLocations?.length ?? 0;
    // const geoLocationCount = campaign?.adGroups?.[0]?.adGroupGeoLocations?.length ?? 0;
    // const audienceSourceCount = campaign?.adGroups?.[0]?.adGroupAudienceSources?.length ?? 0;
    // const inventorySourceCount = campaign?.adGroups?.[0]?.adGroupInventorySources?.length ?? 0;

    const { targetCount: audienceSourceTargetCount } = useCampaignAdGroupAudienceSourceSummary({ campaign });
    const { targetCount: locationTargetCount } = useCampaignAdGroupLocationSummary({ campaign });
    const { targetCount: geoLocationTargetCount } = useCampaignAdGroupGeoLocationSummary({ campaign });
    const { targetCount: inventorySourceTargetCount } = useCampaignAdGroupInventorySourceSummary({ campaign });

    const activeFlightsBudget = (() => {
        if (!campaign.flights?.length) return campaign.budget;
        const now = new Date();
        return campaign.flights
            .filter((f) => !f.endDateTime || new Date(f.endDateTime) >= now)
            .reduce((sum, f) => sum + (f.budgetAmount || 0), 0);
    })();

    return (
        <Card id="campaign-wizard-review-quick-summary" variant="outlined">
            <CardHeader
                title={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                        <Typography variant="body1" color="text.primary">
                            Campaign Summary
                        </Typography>
                    </Stack>
                }
            />
            <CardContent
                sx={{
                    '&': { p: 0 },
                    '&:last-child': { pb: 0 },
                }}
            >
                <Table>
                    <TableBody>
                        <TableRow>
                            <TablePrimaryCell>Advertiser Name</TablePrimaryCell>
                            <TableCell colSpan={4}>{campaign?.dealer?.dealerName}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TablePrimaryCell>Media Type</TablePrimaryCell>
                            <TableCell colSpan={4}>{campaign.mediaType}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TablePrimaryCell>Campaign Strategy</TablePrimaryCell>
                            <TableCell colSpan={4}>{campaign.strategyType}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TablePrimaryCell>Inventory Source</TablePrimaryCell>
                            <TableCell colSpan={4}>
                                <Typography variant="body2">
                                    {inventorySourceTargetCount > 1
                                        ? `${inventorySourceTargetCount} Inventory Sources`
                                        : `${inventorySourceTargetCount} Inventory Source`}
                                    {` `} (All Ad Groups)
                                </Typography>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TablePrimaryCell>Campaign Goal</TablePrimaryCell>
                            <TableCell colSpan={4}>{campaign.goal}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TablePrimaryCell>Start & End Date</TablePrimaryCell>
                            <TableCell colSpan={4}>{`${Utils.formatESTDateLong(
                                campaign.startDate
                            )} - ${Utils.formatESTDateLong(campaign.endDate)}`}</TableCell>
                        </TableRow>
                        <TableRow>
                            <TablePrimaryCell>Budget</TablePrimaryCell>
                            <TableCell colSpan={4}>{Utils.formatCurrency(activeFlightsBudget)}</TableCell>
                        </TableRow>
                        {campaign.flights && campaign.flights.length > 0 && (
                            <TableRow>
                                <TablePrimaryCell sx={{ verticalAlign: 'top' }}>Budget and Flights</TablePrimaryCell>
                                <TableCell colSpan={4}>
                                    <Table
                                        size="small"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            '& .MuiTableCell-root': {
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                            },
                                        }}
                                    >
                                        <TableBody>
                                            <TableRow
                                                sx={{
                                                    backgroundColor: 'grey.100',
                                                    '& .MuiTableCell-root': {
                                                        fontWeight: 600,
                                                    },
                                                }}
                                            >
                                                <TableCell sx={{ width: '80px' }}>Flight No.</TableCell>
                                                <TableCell sx={{ width: '100px' }}>Status</TableCell>
                                                <TableCell sx={{ width: '100px' }}>Budget</TableCell>
                                                <TableCell sx={{ width: '100px' }}>Spent</TableCell>
                                                <TableCell sx={{ width: '100px' }}>Remaining</TableCell>
                                                {userContext.isAdmin() && (
                                                    <TableCell sx={{ width: '120px' }}>Impression Goal</TableCell>
                                                )}
                                                <TableCell sx={{ width: '120px' }}>Start Date</TableCell>
                                                <TableCell sx={{ width: '120px' }}>End date</TableCell>
                                            </TableRow>
                                            {((): JSX.Element[] => {
                                                const now = new Date();

                                                const allFlightsSorted = [...campaign.flights].sort(
                                                    (a, b) =>
                                                        new Date(a.startDateTime).getTime() -
                                                        new Date(b.startDateTime).getTime()
                                                );

                                                const completedCount = allFlightsSorted.filter((f) => {
                                                    if (!f.endDateTime) return false;
                                                    const end = new Date(f.endDateTime);
                                                    return end < now;
                                                }).length;

                                                const activeFlights = allFlightsSorted.filter((f) => {
                                                    if (!f.endDateTime) return true;
                                                    const end = new Date(f.endDateTime);
                                                    return end >= now;
                                                });

                                                // Sort active flights in reverse order for display (newest first)
                                                const displayFlights = [...activeFlights].reverse();

                                                return displayFlights.map((flight, index) => {
                                                    const remaining = flight.budgetAmount - flight.totalCost;

                                                    const displayIndex = activeFlights.length + completedCount - index;

                                                    return (
                                                        <TableRow
                                                            key={flight.id || index}
                                                            sx={{
                                                                backgroundColor:
                                                                    index % 2 === 0 ? 'transparent' : 'grey.50',
                                                            }}
                                                        >
                                                            <TableCell>
                                                                <Typography color="primary" variant="body2">
                                                                    {displayIndex}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                {getFlightStatusBadge(
                                                                    flight.startDateTime,
                                                                    flight.endDateTime
                                                                )}
                                                            </TableCell>
                                                            <TableCell>
                                                                {Utils.formatCurrency(flight.budgetAmount)}
                                                            </TableCell>
                                                            <TableCell>
                                                                {Utils.formatCurrency(flight.totalCost)}
                                                            </TableCell>
                                                            <TableCell>{Utils.formatCurrency(remaining)}</TableCell>
                                                            {userContext.isAdmin() && (
                                                                <TableCell>
                                                                    <Typography color="primary" variant="body2">
                                                                        {flight.impressionGoal != null &&
                                                                        flight.impressionGoal > 0
                                                                            ? flight.impressionGoal.toLocaleString()
                                                                            : '-'}
                                                                    </Typography>
                                                                </TableCell>
                                                            )}
                                                            <TableCell>
                                                                <Typography color="primary" variant="body2">
                                                                    {Utils.formatESTDate(flight.startDateTime)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography color="primary" variant="body2">
                                                                    {Utils.formatESTDate(flight.endDateTime)}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                });
                                            })()}

                                            {campaign.flights.length > 1 &&
                                                (() => {
                                                    const now = new Date();

                                                    const activeFlightsForSummary = campaign.flights.filter((f) => {
                                                        if (!f.endDateTime) return true;
                                                        const end = new Date(f.endDateTime);
                                                        return end >= now;
                                                    });

                                                    if (activeFlightsForSummary.length <= 1) return null;

                                                    const summary = calculateFlightSummary(activeFlightsForSummary);
                                                    return (
                                                        <TableRow
                                                            sx={{
                                                                backgroundColor: 'primary.light',
                                                                '& .MuiTableCell-root': {
                                                                    fontWeight: 600,
                                                                    color: 'primary.main',
                                                                },
                                                            }}
                                                        >
                                                            <TableCell>
                                                                <Typography
                                                                    color="primary"
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                >
                                                                    Total
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell></TableCell>
                                                            <TableCell>
                                                                <Typography
                                                                    color="primary"
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                >
                                                                    {Utils.formatCurrency(summary.totalBudgetAmount)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography
                                                                    color="primary"
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                >
                                                                    {Utils.formatCurrency(summary.totalCost)}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography
                                                                    color="primary"
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                >
                                                                    {Utils.formatCurrency(summary.totalRemaining)}
                                                                </Typography>
                                                            </TableCell>
                                                            {userContext.isAdmin() && (
                                                                <TableCell>
                                                                    <Typography
                                                                        color="primary"
                                                                        variant="body2"
                                                                        fontWeight={600}
                                                                    >
                                                                        {(() => {
                                                                            const total =
                                                                                activeFlightsForSummary.reduce(
                                                                                    (sum, f) =>
                                                                                        sum + (f.impressionGoal ?? 0),
                                                                                    0
                                                                                );
                                                                            return total > 0
                                                                                ? total.toLocaleString()
                                                                                : '-';
                                                                        })()}
                                                                    </Typography>
                                                                </TableCell>
                                                            )}
                                                            <TableCell>
                                                                <Typography
                                                                    color="primary"
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                >
                                                                    {summary.earliestStartDate
                                                                        ? Utils.formatESTDate(summary.earliestStartDate)
                                                                        : '-'}
                                                                </Typography>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Typography
                                                                    color="primary"
                                                                    variant="body2"
                                                                    fontWeight={600}
                                                                >
                                                                    {summary.latestEndDate
                                                                        ? Utils.formatESTDate(summary.latestEndDate)
                                                                        : '-'}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })()}
                                        </TableBody>
                                    </Table>
                                </TableCell>
                            </TableRow>
                        )}
                        <TableRow>
                            <TablePrimaryCell>Targeting (All Ad Groups)</TablePrimaryCell>
                            <TableCell>
                                <Column gap={2}>
                                    <Typography variant="body2">{campaign.zipcodeRadius} Mile Radius</Typography>
                                </Column>
                            </TableCell>
                            <TableCell>
                                <Column gap={2}>
                                    <Typography variant="body2">
                                        {locationTargetCount > 1
                                            ? `${locationTargetCount} Locations`
                                            : `${locationTargetCount} Location`}
                                    </Typography>
                                </Column>
                            </TableCell>
                            <TableCell>
                                <Column gap={2}>
                                    <Typography variant="body2">
                                        {geoLocationTargetCount > 1
                                            ? `${geoLocationTargetCount} Zip Codes`
                                            : `${geoLocationTargetCount} Zip Code`}
                                    </Typography>
                                </Column>
                            </TableCell>
                            <TableCell>
                                <Column gap={2}>
                                    <Typography variant="body2">
                                        {audienceSourceTargetCount > 1
                                            ? `${audienceSourceTargetCount} Audiences`
                                            : `${audienceSourceTargetCount} Audience`}
                                    </Typography>
                                </Column>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TablePrimaryCell>Audience Type</TablePrimaryCell>
                            <TableCell colSpan={4}>
                                <Column gap={2}>
                                    <CampaignAudienceTypeSummary
                                        campaign={campaign}
                                        slotProps={{
                                            typography: {
                                                variant: 'body2',
                                            },
                                        }}
                                    />
                                </Column>
                            </TableCell>
                        </TableRow>
                        <TableRow>
                            <TablePrimaryCell>Creative</TablePrimaryCell>
                            <TableCell colSpan={4}>{getCodeDescription(campaign.campaignType)}</TableCell>
                        </TableRow>
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
}
